import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {TOGGLEABLE_COLUMNS, type ColumnKey} from '../constants/listColumns';
import {EyeIcon} from './EyeIcon';
import {EyeOffIcon} from './EyeOffIcon';

interface Props {
	isVisible: (key: ColumnKey) => boolean;
	onToggle: (key: ColumnKey) => void;
}

// Hidden below the lg breakpoint (Tailwind lg = 1024px), as `hidden lg:block` did.
const Container = styled.div`
	position: relative;
	display: block;
	@media (max-width: 1023px) {
		display: none;
	}
`;
const ToggleButton = styled.button<{$open: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	padding: 8px;
	border: 1px solid ${p => (p.$open ? 'transparent' : '#e2e8f0')};
	background: ${p => (p.$open ? p.theme.ticketing.color.brand50 : '#ffffff')};
	color: ${p => (p.$open ? p.theme.ticketing.color.brand500 : '#334155')};
	cursor: pointer;
	&:hover {
		background: ${p => (p.$open ? p.theme.ticketing.color.brand50 : '#f8fafc')};
	}
`;
const Menu = styled.div`
	position: absolute;
	right: 0;
	z-index: 20;
	margin-top: 4px;
	width: 208px;
	border-radius: ${p => p.theme.ticketing.radius.m};
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 8px 0;
	box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
`;
const MenuHeading = styled.p`
	font: ${p => p.theme.ticketing.font.overline};
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin: 0;
	padding: 0 12px 8px;
	color: ${p => p.theme.ticketing.color.dark500};
`;
const MenuItem = styled.button<{$muted?: boolean}>`
	display: flex;
	width: 100%;
	cursor: pointer;
	align-items: center;
	justify-content: space-between;
	padding: 6px 12px;
	border: 0;
	background: transparent;
	color: ${p => (p.$muted ? p.theme.ticketing.color.dark500 : p.theme.ticketing.color.dark900)};
	&:hover {
		background: #f8fafc;
	}
`;
const ItemLabel = styled.span`
	font: ${p => p.theme.ticketing.font.body2};
`;
const VisibleIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.ticketing.color.dark700};
`;
const HiddenIconWrap = styled.span`
	display: inline-flex;
	color: ${p => p.theme.ticketing.color.dark500};
`;
const Divider = styled.hr`
	margin: 6px 0;
	border: 0;
	border-top: 1px solid ${p => p.theme.ticketing.color.dark100};
`;

export function ColumnsToggle({isVisible, onToggle}: Props) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function onClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') setOpen(false);
		}
		document.addEventListener('mousedown', onClickOutside);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onClickOutside);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const visibleCols = TOGGLEABLE_COLUMNS.filter(col => isVisible(col.key));
	const hiddenCols = TOGGLEABLE_COLUMNS.filter(col => !isVisible(col.key));

	return (
		<Container ref={ref}>
			<ToggleButton
				type="button"
				$open={open}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label="Show/hide columns"
				title="Show/hide columns"
				onClick={() => setOpen(o => !o)}
			>
				<EyeIcon size={16} />
			</ToggleButton>
			{open && (
				<Menu role="menu">
					<MenuHeading>Columns</MenuHeading>
					{visibleCols.map(col => (
						<MenuItem key={col.key} type="button" role="menuitem" onClick={() => onToggle(col.key)}>
							<ItemLabel>{col.label}</ItemLabel>
							<VisibleIconWrap>
								<EyeIcon size={16} data-testid="eye-icon" />
							</VisibleIconWrap>
						</MenuItem>
					))}
					{visibleCols.length > 0 && hiddenCols.length > 0 && <Divider role="separator" />}
					{hiddenCols.map(col => (
						<MenuItem key={col.key} type="button" role="menuitem" $muted onClick={() => onToggle(col.key)}>
							<ItemLabel>{col.label}</ItemLabel>
							<HiddenIconWrap>
								<EyeOffIcon size={16} data-testid="eye-off-icon" />
							</HiddenIconWrap>
						</MenuItem>
					))}
				</Menu>
			)}
		</Container>
	);
}
